import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { memo, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { v4 } from 'uuid'
import { getCouponUsageHistory } from '../../axios/api/developer'
import { downloadInvoice } from '../../axios/api/subscription'
import CustomChip from '../../components/chip'
import CustomDialog from '../../components/dialog'
import Icon from '../../components/icon'
import OverlayLoading from '../../components/overlay-loading'
import {
  downloadPDFBlob,
  formatCurrency,
  formatDateTime,
  showErrorToast
} from '../../utils/utils'

const columns = (tenantList, handleDownloadInvoice) => [
  {
    field: 'tenantId',
    headerName: 'Tenant',
    flex: 0.15,
    minWidth: 150,
    renderCell: ({ value }) => {
      const tenant = tenantList?.find((t) => t?.id === value)
      return (
        <CustomChip
          label={tenant ? tenant?.name : value || '-'}
          color="primary"
          variant="outlined"
          size="small"
          rounded
        />
      )
    }
  },
  {
    field: 'buyerCountry',
    headerName: 'Country',
    flex: 0.1,
    minWidth: 100,
    align: 'right',
    headerAlign: 'right'
  },
  {
    field: 'credits',
    headerName: 'Credits',
    flex: 0.1,
    minWidth: 80,
    align: 'right',
    headerAlign: 'right'
  },
  {
    field: 'baseAmount',
    headerName: 'Amount',
    flex: 0.1,
    minWidth: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ value, row }) => {
      if (value === null || value === undefined || value === '') return '-'

      const amount = Number(value)
      const currency = row?.currency || 'INR'

      if (isNaN(amount)) return '-'

      return formatCurrency(amount, currency)
    }
  },
  {
    field: 'discountAmount',
    headerName: 'Discount',
    flex: 0.1,
    minWidth: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ value, row }) => {
      if (value === null || value === undefined || value === '') return '-'

      const amount = Number(value)
      const currency = row?.currency || 'INR'
      const rate = row?.discountRate ?? null

      if (isNaN(amount)) return '-'

      const formatted = formatCurrency(amount, currency)

      return rate !== null ? `${formatted} (${rate}%)` : formatted
    }
  },
  {
    field: 'invoiceDate',
    headerName: 'Invoice Date',
    align: 'right',
    headerAlign: 'right',
    flex: 0.15,
    minWidth: 120,
    renderCell: ({ value }) => (value ? formatDateTime(value) : '-')
  },
  {
    field: 'invoiceNumber',
    headerName: 'Invoice Number',
    align: 'right',
    headerAlign: 'right',
    flex: 0.1,
    minWidth: 100
  },
  {
    field: 'invoiceId',
    headerName: 'Invoice',
    display: 'flex',
    flex: 0.1,
    renderCell: ({ row, value }) => {
      if (value === null || value === undefined || value === '') return '-'
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        >
          <Icon
            icon={'mdi:download'}
            hoverEffect
            onClick={() => {
              handleDownloadInvoice(value, row?.invoiceNumber)
            }}
            title="Download Invoice"
          />
        </Box>
      )
    }
  }
]

const CouponHistoryModal = ({
  open = false,
  code = null,
  tenantId = null,
  tenantList = [],
  onClose = () => {}
}) => {
  const [loading, setLoading] = useState(false)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [history, setHistory] = useState([])

  const fetchHistory = useCallback(async () => {
    if (!code) return

    setLoading(true)
    const res = await getCouponUsageHistory(code, tenantId || null)
    setLoading(false)

    if (res?.isError) {
      showErrorToast(res)
      return
    }

    const updatedResponse = res?.map((item) => ({
      id: v4(),
      ...item
    }))

    setHistory(updatedResponse || [])
  }, [code, tenantId])

  const handleDownloadInvoice = useCallback(
    async (invoiceId, invoiceNumber) => {
      setInvoiceLoading(true)
      const response = await downloadInvoice(invoiceId)
      setInvoiceLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success('Invoice downloaded successfully')
        downloadPDFBlob(
          response,
          `${invoiceNumber || `invoice_${invoiceId}`}.pdf`
        )
      }
    },
    []
  )

  const memoaziedColumns = useCallback(
    () => columns(tenantList, handleDownloadInvoice),
    [tenantList, handleDownloadInvoice]
  )

  useEffect(() => {
    if (open) fetchHistory()
  }, [open, fetchHistory])

  return (
    <CustomDialog
      open={open}
      handleClose={onClose}
      title={`${code} - Usage History`}
      outsideClikClose={false}
      dialogPaperStyle={{
        width: '95%',
        maxWidth: '1000px'
      }}
    >
      <Box>
        {invoiceLoading && <OverlayLoading />}
        {history?.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }}>
            {loading ? 'Loading...' : 'No usage history found.'}
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {memoaziedColumns()?.map((col) => (
                    <TableCell
                      key={col?.field}
                      align={col?.headerAlign || 'left'}
                      sx={{ minWidth: col?.minWidth }}
                    >
                      {col?.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id} hover>
                    {memoaziedColumns().map((col) => {
                      const value = row?.[col?.field]
                      return (
                        <TableCell
                          key={`${row.id}-${col?.field}`}
                          align={col?.align || 'left'}
                          sx={{ minWidth: col?.minWidth }}
                        >
                          {col?.renderCell
                            ? col?.renderCell({ value, row })
                            : value !== null &&
                              value !== undefined &&
                              value !== ''
                            ? String(value)
                            : '-'}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </CustomDialog>
  )
}

export default memo(CouponHistoryModal)
